/**
 * Account Aggregator Table
 *
 * Displays account aggregators with search and row actions.
 */

import React, { useMemo, useState } from 'react';
import { Table, SearchBar, TableActionsMenu, type TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import type {
  AccountAggregatorTableProps,
  AccountAggregatorRecord,
} from '../../../types/account-aggregator';

const STATUS_ACTIVE = 'ACTIVE';
const STATUS_INACTIVE = 'INACTIVE';

const filterAggregators = (
  records: AccountAggregatorRecord[],
  query: string,
  domain: string
): AccountAggregatorRecord[] => {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) {
    return records;
  }

  return records.filter((record) => {
    const statusLabel = record.status ? STATUS_ACTIVE : STATUS_INACTIVE;
    const channelLabel = record.channel_status ? STATUS_ACTIVE : STATUS_INACTIVE;

    switch (domain) {
      case 'name':
        return record.account_aggregator_name?.toLowerCase().includes(trimmedQuery);
      case 'id':
        return record.account_aggregator_id?.toLowerCase().includes(trimmedQuery);
      case 'handle':
        return record.aa_handle?.toLowerCase().includes(trimmedQuery);
      case 'status':
        return statusLabel.toLowerCase().includes(trimmedQuery);
      case 'channel':
        return channelLabel.toLowerCase().includes(trimmedQuery);
      case 'all':
      default:
        return (
          record.account_aggregator_name?.toLowerCase().includes(trimmedQuery) ||
          record.account_aggregator_id?.toLowerCase().includes(trimmedQuery) ||
          record.aa_handle?.toLowerCase().includes(trimmedQuery) ||
          statusLabel.toLowerCase().includes(trimmedQuery) ||
          channelLabel.toLowerCase().includes(trimmedQuery)
        );
    }
  });
};

export const AccountAggregatorTable: React.FC<AccountAggregatorTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  isLoading,
  permissions,
  onViewDetails,
  onEditDetails,
  onGenerateAuthKey,
  onUpdateAuthKey,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState('all');

  const domains = [
    { value: 'all', label: 'All Fields' },
    { value: 'name', label: 'AA Name' },
    { value: 'id', label: 'AA ID' },
    { value: 'handle', label: 'VUA Handle' },
    { value: 'status', label: 'Status' },
    { value: 'channel', label: 'CR Onboarding' },
  ];

  const filteredData = useMemo(
    () => filterAggregators(data, searchQuery, searchDomain),
    [data, searchQuery, searchDomain]
  );

  const columns = useMemo<TableColumn<AccountAggregatorRecord>[]>(() => [
    {
      key: 'account_aggregator_name',
      header: 'AA Name',
      width: 220,
      render: (_value, row) => <span className="text-sm">{row.account_aggregator_name}</span>,
    },
    {
      key: 'account_aggregator_id',
      header: 'AA ID',
      width: 160,
      render: (_value, row) => (
        <span className="text-sm font-mono">{row.account_aggregator_id}</span>
      ),
    },
    {
      key: 'aa_handle',
      header: 'VUA Handle',
      width: 200,
      render: (_value, row) => <span className="text-sm">{row.aa_handle || 'Not Available'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: 140,
      render: (_value, row) => (
        <span
          className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
            row.status
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}
        >
          {row.status ? STATUS_ACTIVE : STATUS_INACTIVE}
        </span>
      ),
    },
    {
      key: 'channel_status',
      header: 'CR Onboarding',
      width: 160,
      render: (_value, row) => (
        <span
          className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${
            row.channel_status
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}
        >
          {row.channel_status ? STATUS_ACTIVE : STATUS_INACTIVE}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      isActions: true,
      width: 160,
      align: 'center',
      render: (_value, row) => {
        const actions = [];

        if (permissions.canView) {
          actions.push({
            key: 'view',
            label: 'View details',
            onClick: () => onViewDetails(row),
          });
        }

        if (permissions.canEdit && permissions.isSuperAdmin) {
          actions.push({
            key: 'edit',
            label: 'Edit details',
            onClick: () => onEditDetails(row),
          });
          actions.push({
            key: 'generateKey',
            label: 'Generate auth key',
            onClick: () => onGenerateAuthKey(row),
          });
          actions.push({
            key: 'updateKey',
            label: 'Update outbound key',
            onClick: () => onUpdateAuthKey(row),
          });
        }

        if (actions.length === 0) {
          return <span className="text-sm text-text-secondary">No actions</span>;
        }

        return (
          <TableActionsMenu actions={actions} srLabel={`Actions for ${row.account_aggregator_name}`} />
        );
      },
    },
  ], [onEditDetails, onGenerateAuthKey, onUpdateAuthKey, onViewDetails, permissions]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-sm bg-white">
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1">
          <SearchBar
            variant="with-dropdown"
            domains={domains}
            value={searchQuery}
            onChange={setSearchQuery}
            selectedDomain={searchDomain}
            onDomainChange={setSearchDomain}
            placeholder="Search account aggregators..."
          />
        </div>
      </div>

      <Table
        data={filteredData}
        columns={columns}
        loading={isLoading}
        pagination={{
          currentPage,
          pageSize,
          totalItems,
          onPageChange,
          onPageSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
        columnManager={{
          enabled: true,
          allowHiding: false,
        }}
      />
    </div>
  );
};
